import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganizationWorkspaces } from '@/entities/user';
import { knowledgeBaseWorkspacesQuery } from '../../api/knowledgeBaseWorkspaces.api';
import type { KnowledgeBaseDto } from '../knowledge-bases.types';

/**
 * Who a knowledge base is shared with, in words, for the collection info. Undefined for one that is
 * not shared with chosen workspaces, so the row is left out rather than saying nothing.
 *
 * Workspaces the caller cannot reach arrive as a count only, and are reported as such: naming
 * them would tell a caller about workspaces they are not in.
 */
export const useKbSharedWith = (
  knowledgeBase: KnowledgeBaseDto | undefined,
): string | undefined => {
  const { t } = useTranslation('knowledge');
  const workspaces = useCurrentOrganizationWorkspaces();

  const { data: scope } = useQuery({
    ...knowledgeBaseWorkspacesQuery(knowledgeBase?.id),
    enabled: !knowledgeBase?.isInternal && knowledgeBase?.visibility === 'workspaces',
  });

  if (!scope || scope.visibility !== 'workspaces') return undefined;

  const names = scope.workspaceIds
    .map((id) => workspaces.find((workspace) => workspace.id === id)?.name)
    .filter((name): name is string => !!name);
  const hidden = scope.inaccessibleWorkspaceCount
    ? t('knowledgeBases.knowledgeBaseInfoPopover.sharedWithHidden', {
        count: scope.inaccessibleWorkspaceCount,
      })
    : undefined;

  return [names.join(', '), hidden].filter(Boolean).join(' ') || undefined;
};
